import { type ButtonHTMLAttributes, forwardRef } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
    | 'secondary-on-dark' | 'ghost-on-dark';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  loading?: boolean;
  fullWidth?: boolean;
}

// The app is light-themed (bg-page). `secondary` / `ghost` are light-surface
// variants; the `-on-dark` pair is for the pages still rendered on bg-navy
// (worksheet preview). Using a light variant on navy — or a dark
// one on the page background — produces ~1:1 contrast, which is how the
// "back to home" button on the lesson-complete screen went invisible.
//
// Teal fills carry navy text, never white: white-on-teal measures 2.26:1 and
// fails WCAG AA, while navy-on-teal measures 7.69:1.
const variantClasses = {
  primary:
    'bg-teal text-navy font-semibold hover:bg-teal-light active:bg-teal-dark shadow-glow hover:shadow-glow-lg',
  secondary:
    'border-2 border-ink/15 text-ink bg-white font-semibold hover:border-teal/50 hover:bg-teal/5 active:bg-teal/10',
  ghost:
    'text-ink/70 hover:text-ink hover:bg-ink/5 active:bg-ink/10',
  danger:
    'bg-red-500 text-white hover:bg-red-600 active:bg-red-700',
  'secondary-on-dark':
    'border border-white/20 text-chalk hover:bg-white/10 active:bg-white/5',
  'ghost-on-dark':
    'text-chalk/70 hover:text-chalk hover:bg-white/10 active:bg-white/5',
};

const sizeClasses = {
  sm: 'px-3 py-1.5 text-sm rounded-lg',
  md: 'px-5 py-2.5 text-sm rounded-xl',
  lg: 'px-7 py-3.5 text-base rounded-xl',
  xl: 'px-8 py-4 text-lg rounded-2xl',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      fullWidth = false,
      className = '',
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        className={[
          'inline-flex items-center justify-center gap-2',
          'font-display transition-all duration-200',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/50',
          'disabled:opacity-40 disabled:cursor-not-allowed',
          variantClasses[variant],
          sizeClasses[size],
          fullWidth ? 'w-full' : '',
          className,
        ].join(' ')}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <svg
            className="animate-spin -ml-1 mr-1 h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
